import * as React from 'react';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

/** Material UI Packages. */
import { Button, Typography, Paper } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { GridToolbarContainer, useGridApiRef } from '@mui/x-data-grid-pro';
import { randomId } from '@mui/x-data-grid-generator';

/** Material UI Icons. */
import AddIcon from '@mui/icons-material/Add';

/** Redux Packages. */
import { useSelector, useDispatch } from 'react-redux';

/** Internal Components. */
import { handleTabKeyDown } from 'shared/ui/Datagrid';
import { CustomSkeleton2 } from 'shared/ui/Skeleton';
import useInvalidOptionWarning from 'shared/hooks/useInvalidOptionWarning';

import { getRateColumns, NOTE_CELL_METRICS } from './RateDef';
import { preventNoteEnterLineBreak } from 'shared/ui/MultilineNoteEditCell';
import { createSocketData, readSocketData, deleteSocketData, handleProcessRowUpdate } from 'shared/lib/fetchSocket';
import { splitClipboardPastedText, makeQuotedClipboardCopyHandler } from 'shared/lib/gridClipboard';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';
import { addRate as addOimRate, deleteRate as deleteOimRate } from 'shared/store/slices/oim-rateUpdateSlice';
import { addRate as addOexRate, deleteRate as deleteOexRate } from 'shared/store/slices/oex-rateUpdateSlice';
import { addRate as addAimRate, deleteRate as deleteAimRate } from 'shared/store/slices/aim-rateUpdateSlice';
import { addRate as addAexRate, deleteRate as deleteAexRate } from 'shared/store/slices/aex-rateUpdateSlice';
import {
    addRate as addTruckingRate,
    deleteRate as deleteTruckingRate,
} from 'shared/store/slices/trucking-rateUpdateSlice';

const addRateActions = { 0: addOimRate, 1: addOexRate, 2: addAimRate, 3: addAexRate, 4: addTruckingRate };
const deleteRateActions = {
    0: deleteOimRate,
    1: deleteOexRate,
    2: deleteAimRate,
    3: deleteAexRate,
    4: deleteTruckingRate,
};

/** Mapping of reducer names for different shipment types. */
const reducerName = {
    0: ['setOimRateUpdate', 'addOimRate', 'deleteOimRate'],
    1: ['setOexRateUpdate', 'addOexRate', 'deleteOexRate'],
    2: ['setAimRateUpdate', 'addAimRate', 'deleteAimRate'],
    3: ['setAexRateUpdate', 'addAexRate', 'deleteAexRate'],
    4: ['setTruckingRateUpdate', 'addTruckingRate', 'deleteTruckingRate'],
};
/** Mapping of key names for different shipment types. */
const keyName = {
    0: ['il_ocean_import_id', 'oimRateUpdateForm'],
    1: ['il_ocean_export_id', 'oexRateUpdateForm'],
    2: ['il_air_import_id', 'aimRateUpdateForm'],
    3: ['il_air_export_id', 'aexRateUpdateForm'],
    4: ['il_ftl_ltl_id', 'truckingRateUpdateForm'],
};

/** Returns true when `id` is a temporary local ID (string), not a DB integer. */
const isUnsaved = (id) => typeof id === 'string';

/** Toolbar component for adding new rows. */
function EditToolbar(props) {
    const { dispatch, row, tabValue, header, showLabel, apiRef } = props;

    const handleClick = () => {
        const id = randomId(); // UUID string — distinguishable from integer DB IDs
        const matchedKeyname = keyName[tabValue][0];
        const newRate = { id, [matchedKeyname]: row.id, il_rate_type_id: null, rate: '', note: '', category: header };

        // Add locally only — backend save happens on processRowUpdate.
        dispatch(addRateActions[tabValue](newRate));

        // New row renders at the bottom (sorted last, see rateRow sort below) — open its
        // first cell in edit mode once the grid has re-rendered with the row present.
        requestAnimationFrame(() => {
            try {
                apiRef.current?.startCellEditMode({ id, field: 'il_rate_type_id' });
            } catch (error) {
                // Row not mounted yet — ignore, user can click the cell to edit.
            }
        });
    };

    return (
        <GridToolbarContainer sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Button
                sx={{ color: 'white', fontSize: '12px', bgcolor: '#00647e', p: 0, py: 0.3, mb: 0.5 }}
                startIcon={<AddIcon />}
                onClick={handleClick}
            >
                Add
            </Button>
            {showLabel && (
                <Typography
                    sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        textAlign: 'center',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: '#003a52',
                        pointerEvents: 'none',
                    }}
                >
                    {header}
                </Typography>
            )}
        </GridToolbarContainer>
    );
}

export default function UpdateRateInfoTable(props) {
    /** Get props from UpdateRateInfo.js */
    const { row, header } = props;

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();
    /** Create a reference to the Datagrid API.  */
    const apiRef = useGridApiRef();

    /** Shipment type data. */
    const tabValue = useSelector((state) => state.tabValueForm);
    /** Function to dynamically select a state. */
    const useDynamicSelector = (stateKey) => {
        const dynamicStateKey = `${stateKey}`;
        return useSelector((state) => state[dynamicStateKey]);
    };
    /** Original rate data based on shipment type. */
    const rateData = useDynamicSelector(keyName[tabValue][1]);

    /** State for rate datagird rows. */
    const [rateRow, setRateRow] = useState([]);
    const [rowModesModel, setRowModesModel] = useState({});
    /** Options for the type autocomplete (last search / initial full active list). */
    const [rateType, setRateType] = useState([]);
    /** Accumulated id → { id, name } for labels so search-narrowed options do not hide other rows' type names. */
    const [rateTypeLookupList, setRateTypeLookupList] = useState([]);
    /** True until both rate-type lookups (for the autocomplete + label resolution) have loaded at least once. */
    const [activeTypesLoaded, setActiveTypesLoaded] = useState(false);
    const [allTypesLoaded, setAllTypesLoaded] = useState(false);
    const isLoading = !activeTypesLoaded || !allTypesLoaded;

    const handleInvalidOption = useInvalidOptionWarning();

    const mergeRateTypesIntoLookup = useCallback((list) => {
        if (!list?.length) return;
        setRateTypeLookupList((prev) => {
            const m = new Map(prev.map((t) => [t.id, t]));
            list.forEach((t) => {
                if (t?.id != null) m.set(t.id, t);
            });
            return Array.from(m.values());
        });
    }, []);

    /**
     * Cleanup ref — always points to a function that deletes any unsaved (string-ID)
     * rows from Redux. Updated every render so the cleanup on unmount uses current values.
     */
    const cleanupRef = useRef(null);
    cleanupRef.current = () => {
        rateData?.forEach((item) => {
            if (isUnsaved(item.id)) {
                dispatch(deleteRateActions[tabValue](item.id));
            }
        });
    };
    useEffect(() => () => cleanupRef.current?.(), []);

    /** Preload active rate types for the autocomplete dropdown (cell = active only). */
    useEffect(() => {
        setActiveTypesLoaded(false);
        readSocketData(dispatch, tabValue, 'cell', 'il_rate_type', '', '', (receivedData) => {
            setRateType(receivedData || []);
            setActiveTypesLoaded(true);
        });
    }, [dispatch, tabValue]);

    /** Preload ALL rate types (including inactive) for label resolution of existing rows. */
    useEffect(() => {
        setAllTypesLoaded(false);
        readSocketData(dispatch, tabValue, 'row', 'il_rate_type', '', '', (receivedData) => {
            mergeRateTypesIntoLookup(receivedData || []);
            setAllTypesLoaded(true);
        });
    }, [dispatch, tabValue, mergeRateTypesIntoLookup]);

    useEffect(() => {
        if (rateData) {
            const matchedKeyname = keyName[tabValue][0];
            const parentId = row?.id;
            const matchedList = rateData
                ?.filter((item) => String(item[matchedKeyname]) === String(parentId) && item.category === header)
                .sort((a, b) => {
                    // Unsaved rows (string/UUID id) have no real ordering yet — always keep
                    // them at the bottom, in the order they were added (stable sort).
                    const aIsUnsaved = isUnsaved(a.id);
                    const bIsUnsaved = isUnsaved(b.id);
                    if (aIsUnsaved && bIsUnsaved) return 0;
                    if (aIsUnsaved) return 1;
                    if (bIsUnsaved) return -1;
                    return a.id - b.id;
                });

            setRateRow(matchedList);
        }
    }, [rateData, row?.id, tabValue, header]);

    const pendingFocusIdRef = useRef(null);

    useEffect(() => {
        const pendingId = pendingFocusIdRef.current;
        if (pendingId == null || !rateRow.some((item) => item.id === pendingId)) return;
        pendingFocusIdRef.current = null;
        apiRef.current?.setCellFocus(pendingId, 'il_rate_type_id');
        apiRef.current?.setCellSelectionModel?.({ [pendingId]: { il_rate_type_id: true } });
    }, [rateRow, apiRef]);

    /** Handle to change autocomplete rate type and call Websocket to fetch the searched result. */
    const handleRateChange = (event, value) => {
        readSocketData(dispatch, tabValue, 'cell', 'il_rate_type', '', value, (receivedData) => {
            const list = receivedData || [];
            mergeRateTypesIntoLookup(list);
            setRateType(list);
        });
    };

    /** Handle row deletion — local-only for unsaved rows, socket for persisted rows. */
    const handleDeleteClick = (id) => () => {
        if (isUnsaved(id)) {
            dispatch(deleteRateActions[tabValue](id));
            return;
        }
        const reducerString = reducerName[tabValue][2];
        if (window.confirm('Are you sure you want to delete the entry?')) {
            deleteSocketData(dispatch, tabValue, 'row', 'il_rate', reducerString, id);
        }
    };

    /** Note 안의 줄바꿈이 행 구분과 섞이지 않도록 CSV 식으로 감싸 복사한다(shared/lib/gridClipboard). */
    const handleClipboardCopy = useMemo(() => makeQuotedClipboardCopyHandler(apiRef), [apiRef]);

    /** 셀 키 입력: Note 를 Enter 로 열 때 줄바꿈이 끼어드는 것을 막고, 나머지는 기존 Tab 이동 처리에 넘긴다. */
    const handleCellKeyDown = useCallback(
        (params, event) => {
            preventNoteEnterLineBreak(params, event);
            handleTabKeyDown(apiRef)?.(params, event);
        },
        [apiRef]
    );

    /** Handle to reflect the editing to datagrid when the row changes. */
    const handleRowModesModelChange = (newRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const rateCol = getRateColumns(rateType, rateTypeLookupList, handleRateChange, handleDeleteClick, handleInvalidOption);

    const isOcean = tabValue === 0 || tabValue === 1;

    if (isLoading) {
        return (
            <Paper sx={{ width: '100%', m: 0.5, borderRadius: '15px', p: 1, minHeight: '300px', height: '100%' }}>
                {!isOcean && (
                    <Typography
                        sx={{ fontSize: '14px', fontWeight: 'bold', color: '#003a52', p: 1, textAlign: 'center' }}
                    >
                        {header}
                    </Typography>
                )}
                <CustomSkeleton2 />
            </Paper>
        );
    }

    return (
        <Paper
            sx={{
                width: '100%',
                m: 0.5,
                borderRadius: '15px',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            {!isOcean && (
                <Typography sx={{ fontSize: '14px', fontWeight: 'bold', color: '#003a52', p: 1, textAlign: 'center' }}>
                    {header}
                </Typography>
            )}
            <DataGridPremium
                rows={rateRow}
                columns={rateCol}
                density="compact"
                editMode="cell"
                cellSelection
                rowSelection={false}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            id: false,
                        },
                    },
                }}
                disableColumnMenu
                disableColumnSorting
                hideFooter
                processRowUpdate={(newRow) => {
                    if (isUnsaved(newRow.id)) {
                        if (newRow.il_rate_type_id == null || newRow.il_rate_type_id === '') {
                            dispatch(
                                setSnackbar({
                                    open: true,
                                    message: 'Please select a rate type.',
                                    severity: 'warning',
                                })
                            );
                            return Promise.resolve(rateRow.find((r) => r.id === newRow.id) || newRow);
                        }
                        const matchedReducer = reducerName[tabValue][1];
                        const tempId = newRow.id;
                        return new Promise((resolve) => {
                            createSocketData(
                                dispatch,
                                tabValue,
                                'row',
                                'il_rate',
                                matchedReducer,
                                newRow,
                                (response) => {
                                    dispatch(deleteRateActions[tabValue](tempId));
                                    const created = response?.data;
                                    if (created && created.id != null) {
                                        dispatch(addRateActions[tabValue](created));
                                        // Hand the new id to the re-focus effect above — the
                                        // cell the user was editing is about to disappear.
                                        pendingFocusIdRef.current = created.id;
                                        try {
                                            apiRef.current?.updateRows([{ id: tempId, _action: 'delete' }]);
                                        } catch (error) {
                                            // Grid unmounted — its cache is gone anyway.
                                        }
                                        // Resolve with the server row (real id), not the stale
                                        // temp-id row — MUI applies this via apiRef.updateRows(),
                                        // which would otherwise re-insert a phantom temp-id row
                                        // into the grid's internal cache after Redux already
                                        // dropped it, leaving a ghost row in the DOM.
                                        resolve(created);
                                    } else {
                                        resolve(newRow);
                                    }
                                },
                                () => resolve(newRow)
                            );
                        });
                    }
                    
                    let rowToSave = newRow;
                    if (newRow.il_rate_type_id == null || newRow.il_rate_type_id === '') {
                        dispatch(
                            setSnackbar({
                                open: true,
                                message: 'Rate type is required.',
                                severity: 'warning',
                            })
                        );
                        const previousType = rateData?.find((r) => r.id === newRow.id)?.il_rate_type_id ?? null;
                        rowToSave = { ...newRow, il_rate_type_id: previousType };
                    }

                    return handleProcessRowUpdate({
                        dispatch,
                        tabValue,
                        methodTableName: 'il_rate',
                        reducerName: reducerName[tabValue][0],
                        tableId: [],
                        formData: rateData,
                        newRow: rowToSave,
                        notAllowNullKey: ['il_rate_type_id'],
                        dateField: [],
                    });
                }}
                splitClipboardPastedText={splitClipboardPastedText}
                onClipboardCopy={handleClipboardCopy}
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                slots={{
                    toolbar: EditToolbar,
                }}
                slotProps={{
                    toolbar: { dispatch, row, tabValue, header, showLabel: isOcean, apiRef },
                }}
                getRowHeight={() => 'auto'}
                sx={{
                    flex: 1,
                    minHeight: '300px',
                    fontSize: '12px',
                    borderRadius: '10px',
                    overflow: 'auto',
                    border: 'none',
                    '& .MuiDataGrid-columnHeader': {
                        backgroundColor: '#dfdfdf',
                    },
                    '& .MuiDataGrid-cell': {
                        border: '0.5px solid #dfdfdf',
                        p: 0,
                        py: '1px',
                    },
                    // Note column: wrap long text instead of clipping it with an ellipsis.
                    // 보기/편집 모두 `NOTE_CELL_METRICS` 의 같은 여백을 써서 편집 전후 행 높이를 맞춘다.
                    '& .MuiDataGrid-cell[data-field="note"]': {
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        lineHeight: NOTE_CELL_METRICS.lineHeight,
                        py: NOTE_CELL_METRICS.padY,
                        px: NOTE_CELL_METRICS.padX,
                    },
                    '& .MuiDataGrid-cell--editing[data-field="note"]': {
                        alignItems: 'flex-start',
                        py: NOTE_CELL_METRICS.padY,
                        px: NOTE_CELL_METRICS.padX,
                    },
                    '& .MuiSelect-select': {
                        fontSize: '14px',
                    },
                }}
                apiRef={apiRef}
                onCellKeyDown={handleCellKeyDown}
            />
        </Paper>
    );
}
